/* ==========================================================================
   FILE: global/components/Input.jsx
   IAMONEAI - Input Component
   Text input, textarea, and search input with validation states
   ========================================================================== */

import React, { forwardRef, useState, useId } from 'react';
import './Input.css';

// ==========================================================================
// TEXT INPUT COMPONENT
// ==========================================================================

/**
 * Text input component with label, error, and icon support
 * 
 * @param {Object} props
 * @param {string} label - Input label
 * @param {string} error - Error message
 * @param {string} hint - Hint text below input
 * @param {'sm'|'md'|'lg'} size - Input size
 * @param {React.ReactNode} leftIcon - Icon on the left
 * @param {React.ReactNode} rightIcon - Icon on the right
 * @param {boolean} fullWidth - Whether input takes full width
 * @param {string} className - Additional CSS classes
 * 
 * @example
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   error={errors.email}
 * />
 */
const Input = forwardRef(({
  label,
  error,
  hint,
  size = 'md',
  leftIcon = null,
  rightIcon = null,
  fullWidth = true,
  className = '',
  id: providedId,
  disabled = false,
  required = false,
  type = 'text',
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const hasError = Boolean(error);

  const wrapperClasses = [
    'input-wrapper',
    fullWidth && 'input-full-width',
    className,
  ].filter(Boolean).join(' ');

  const inputClasses = [
    'input',
    `input-${size}`,
    hasError && 'input-error',
    disabled && 'input-disabled',
    leftIcon && 'input-with-left-icon',
    rightIcon && 'input-with-right-icon',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {/* Label */}
      {label && (
        <label htmlFor={id} className="input-label">
          {label}
          {required && <span className="input-required" aria-hidden="true">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className="input-container">
        {/* Left Icon */}
        {leftIcon && (
          <span className="input-icon input-icon-left" aria-hidden="true">
            {leftIcon}
          </span>
        )}

        {/* Input Element */}
        <input
          ref={ref}
          id={id}
          type={type}
          className={inputClasses}
          disabled={disabled}
          required={required}
          aria-invalid={hasError}
          aria-describedby={
            [hasError && errorId, hint && hintId].filter(Boolean).join(' ') || undefined
          }
          {...props}
        />

        {/* Right Icon */}
        {rightIcon && (
          <span className="input-icon input-icon-right" aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </div>

      {/* Error Message */}
      {hasError && (
        <p id={errorId} className="input-error-message" role="alert">
          {error}
        </p>
      )}

      {/* Hint Text */}
      {hint && !hasError && (
        <p id={hintId} className="input-hint">
          {hint}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

// ==========================================================================
// TEXTAREA COMPONENT
// ==========================================================================

/**
 * Textarea component with auto-resize option
 * 
 * @example
 * <Textarea
 *   label="Message"
 *   placeholder="Type your message..."
 *   rows={4}
 *   autoResize
 * />
 */
export const Textarea = forwardRef(({
  label,
  error,
  hint,
  size = 'md',
  fullWidth = true,
  className = '',
  id: providedId,
  disabled = false,
  required = false,
  rows = 3,
  autoResize = false,
  maxRows = 10,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const hasError = Boolean(error);

  const wrapperClasses = [
    'input-wrapper',
    fullWidth && 'input-full-width',
    className,
  ].filter(Boolean).join(' ');

  const textareaClasses = [
    'textarea',
    `textarea-${size}`,
    hasError && 'input-error',
    disabled && 'input-disabled',
    autoResize && 'textarea-auto-resize',
  ].filter(Boolean).join(' ');

  // Handle auto-resize
  const handleInput = (e) => {
    if (autoResize) {
      const textarea = e.target;
      textarea.style.height = 'auto';
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
      const maxHeight = lineHeight * maxRows;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
    props.onInput?.(e);
  };

  return (
    <div className={wrapperClasses}>
      {/* Label */}
      {label && (
        <label htmlFor={id} className="input-label">
          {label}
          {required && <span className="input-required" aria-hidden="true">*</span>}
        </label>
      )}

      {/* Textarea Element */}
      <textarea
        ref={ref}
        id={id}
        className={textareaClasses}
        disabled={disabled}
        required={required}
        rows={rows}
        aria-invalid={hasError}
        aria-describedby={
          [hasError && errorId, hint && hintId].filter(Boolean).join(' ') || undefined
        }
        onInput={handleInput}
        {...props}
      />

      {/* Error Message */}
      {hasError && (
        <p id={errorId} className="input-error-message" role="alert">
          {error}
        </p>
      )}

      {/* Hint Text */}
      {hint && !hasError && (
        <p id={hintId} className="input-hint">
          {hint}
        </p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

// ==========================================================================
// SEARCH INPUT COMPONENT
// ==========================================================================

/**
 * Search input with search icon and clear button
 * 
 * @example
 * <SearchInput
 *   placeholder="Search memories..."
 *   value={searchTerm}
 *   onChange={(e) => setSearchTerm(e.target.value)}
 *   onClear={() => setSearchTerm('')}
 * />
 */
export const SearchInput = forwardRef(({
  size = 'md',
  fullWidth = true,
  className = '',
  value = '',
  onClear,
  ...props
}, ref) => {
  const hasValue = value && value.length > 0;

  const wrapperClasses = [
    'search-input-wrapper',
    fullWidth && 'input-full-width',
    className,
  ].filter(Boolean).join(' ');

  const inputClasses = [
    'input',
    'search-input',
    `input-${size}`,
    'input-with-left-icon',
    hasValue && 'input-with-right-icon',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {/* Search Icon */}
      <span className="input-icon input-icon-left" aria-hidden="true">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      </span>

      {/* Input */}
      <input
        ref={ref}
        type="search"
        className={inputClasses}
        value={value}
        {...props}
      />

      {/* Clear Button */}
      {hasValue && onClear && (
        <button
          type="button"
          className="search-clear-btn"
          onClick={onClear}
          aria-label="Clear search"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      )}
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

// ==========================================================================
// PASSWORD INPUT COMPONENT
// ==========================================================================

/**
 * Password input with show/hide toggle
 * 
 * @example
 * <PasswordInput
 *   label="Password"
 *   placeholder="Enter password"
 * />
 */
export const PasswordInput = forwardRef(({
  label,
  error,
  hint,
  size = 'md',
  fullWidth = true,
  className = '',
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);

  const toggleVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const eyeIcon = showPassword ? (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  return (
    <Input
      ref={ref}
      type={showPassword ? 'text' : 'password'}
      label={label}
      error={error}
      hint={hint}
      size={size}
      fullWidth={fullWidth}
      className={className}
      rightIcon={
        <button
          type="button"
          className="password-toggle-btn"
          onClick={toggleVisibility}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {eyeIcon}
        </button>
      }
      {...props}
    />
  );
});

PasswordInput.displayName = 'PasswordInput';

// ==========================================================================
// CODE INPUT COMPONENT (for verification codes)
// ==========================================================================

/**
 * 6-digit code input for verification
 * 
 * @example
 * <CodeInput
 *   value={code}
 *   onChange={setCode}
 *   length={6}
 *   error={codeError}
 * />
 */
export const CodeInput = forwardRef(({
  value = '',
  onChange,
  length = 6,
  error,
  disabled = false,
  className = '',
  autoFocus = true,
}, ref) => {
  const [focused, setFocused] = useState(false);

  const handleChange = (e) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, length);
    onChange?.(newValue);
  };

  const handleKeyDown = (e) => {
    // Allow backspace to work naturally
    if (e.key === 'Backspace') {
      return;
    }
    // Only allow numbers
    if (!/^\d$/.test(e.key) && !['Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange?.(pastedData);
  };

  const wrapperClasses = [
    'code-input-wrapper',
    focused && 'code-input-focused',
    error && 'code-input-error',
    disabled && 'code-input-disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {/* Visual boxes */}
      <div className="code-input-boxes">
        {Array.from({ length }).map((_, index) => (
          <div
            key={index}
            className={[
              'code-input-box',
              value[index] && 'code-input-box-filled',
              focused && index === value.length && 'code-input-box-active',
            ].filter(Boolean).join(' ')}
          >
            {value[index] || ''}
          </div>
        ))}
      </div>

      {/* Hidden actual input */}
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        className="code-input-hidden"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        autoFocus={autoFocus}
        maxLength={length}
      />

      {/* Error */}
      {error && (
        <p className="input-error-message" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

CodeInput.displayName = 'CodeInput';

// ==========================================================================
// EXPORTS
// ==========================================================================

export default Input;